import type { RailBranch } from "@/data/rail-branches";
import type { SceneBeat } from "@/lib/timeline/book/BookLayout";
import { locationPathOf, type LocationRun } from "./runs";

/** One box per rail branch, so a place left behind stays beside the fork. */
export function locationRunsFromRail(
  branches: readonly RailBranch[],
  beats: readonly SceneBeat[],
): LocationRun[] {
  const cached = railRunsByBranches.get(branches);
  if (cached?.beats === beats) {
    return cached.runs;
  }
  const runs = computeRailRuns(branches, beats);
  railRunsByBranches.set(branches, { beats, runs });
  return runs;
}

const railRunsByBranches = new WeakMap<
  readonly RailBranch[],
  { beats: readonly SceneBeat[]; runs: LocationRun[] }
>();

function computeRailRuns(
  branches: readonly RailBranch[],
  beats: readonly SceneBeat[],
): LocationRun[] {
  return branches.map((branch) => {
    const nested = nestedLabelForBranch(branch, beats);
    return {
      runId: branch.id,
      locationLabel: branch.label,
      startMs: branch.startMs,
      endMs: branch.endMs,
      lane: branch.lane,
      ...(nested === undefined ? {} : { nestedLabel: nested }),
    };
  });
}

function nestedLabelForBranch(
  branch: RailBranch,
  beats: readonly SceneBeat[],
): string | undefined {
  const labels = beats
    .filter((beat) => beat.startMs < branch.endMs && beat.endMs > branch.startMs)
    .filter((beat) => locationPathOf(beat)[0] === branch.label)
    .map((beat) => {
      const path = locationPathOf(beat);
      return path.length > 1 ? path[path.length - 1] : undefined;
    });
  const first = labels[0];
  if (first === undefined) {
    return undefined;
  }
  if (labels.every((label) => label === first)) {
    return first;
  }
  return undefined;
}
